/**
 * UploadProgressBar.tsx
 *
 * Upload progress indicator component for file uploads.
 * Shows progress bar, file info, and optional cancel button.
 *
 * Features:
 * - Real-time progress display (0-100%)
 * - File name and size display
 * - Optional estimated time remaining
 * - Optional cancel button
 */

import { FC } from 'react'

export type UploadProgressMode = 'uploading' | 'sending' | 'converting' | 'paused' | 'slow'

export interface UploadProgressBarProps {
  /** Upload progress percentage (0-100) */
  progress: number
  /** Name of file being uploaded */
  fileName: string
  /** Size of file being uploaded (formatted string, e.g., "2.5 MB") */
  fileSize: string
  /** Mode: 'uploading' shows progress bar, 'sending' shows spinner only */
  mode?: UploadProgressMode
  /** Platform name for sending mode (e.g., "tiktok", "whatsapp") */
  platform?: string
  /** Optional: Estimated time remaining (formatted string, e.g., "30 seconds") */
  estimatedTime?: string
  /** Optional: Callback when cancel button clicked */
  onCancel?: () => void
}

const UploadProgressBar: FC<UploadProgressBarProps> = ({
  progress,
  fileName,
  fileSize,
  mode = 'uploading',
  platform,
  estimatedTime,
  onCancel,
}) => {
  // Ensure progress is within 0-100 range
  const normalizedProgress = Math.min(100, Math.max(0, progress))
  const progressPercent = Math.round(normalizedProgress)

  // Capitalize platform name for display
  const platformName = platform
    ? platform.charAt(0).toUpperCase() + platform.slice(1)
    : 'platform'

  // Determine display text based on mode
  const statusText = (() => {
    switch (mode) {
      case 'sending':
        return `Sending to ${platformName}...`
      case 'converting':
        return 'Preparing image...'
      case 'paused':
        return 'Upload paused (screen locked)'
      case 'slow':
        return 'Upload may be slow (battery saver mode)'
      case 'uploading':
      default:
        return `Uploading... ${progressPercent}%`
    }
  })()

  // Determine if progress bar should be shown
  const showProgressBar = mode === 'uploading'

  // Determine if cancel button should be shown
  const showCancelButton = onCancel && (mode === 'uploading' || mode === 'slow')

  return (
    <div
      className="mb-2 p-3 bg-light rounded"
      data-testid="upload-progress-container"
      style={{ borderLeft: '4px solid #0d6efd' }}
    >
      {/* Header: File info and cancel button */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div
            className="text-truncate fw-bold"
            style={{ fontSize: '14px' }}
            title={fileName}
            data-testid="upload-file-name"
          >
            {fileName}
          </div>
          <div className="text-muted" style={{ fontSize: '12px' }}>
            {fileSize}
          </div>
        </div>

        {showCancelButton && (
          <button
            type="button"
            className="btn btn-sm btn-light ms-2"
            onClick={onCancel}
            title="Cancel upload"
            data-testid="upload-cancel-button"
            style={{ flexShrink: 0 }}
          >
            <i className="bi bi-x-circle" />
          </button>
        )}
      </div>

      {/* Progress bar - only show when uploading */}
      {showProgressBar && (
        <div className="progress mb-2" style={{ height: '8px' }}>
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            aria-valuenow={progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            style={{ width: `${progressPercent}%` }}
            data-testid="upload-progress-bar"
          />
        </div>
      )}

      {/* Footer: Status text with icon based on mode */}
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          {/* Show icon based on mode */}
          {mode === 'sending' && (
            <div
              className="spinner-border spinner-border-sm text-primary"
              role="status"
              style={{ width: '16px', height: '16px' }}
              data-testid="upload-sending-spinner"
            >
              <span className="visually-hidden">Sending...</span>
            </div>
          )}

          {mode === 'converting' && (
            <div
              className="spinner-border spinner-border-sm text-primary"
              role="status"
              style={{ width: '16px', height: '16px' }}
              data-testid="upload-converting-spinner"
            >
              <span className="visually-hidden">Converting...</span>
            </div>
          )}

          {mode === 'paused' && (
            <i
              className="bi bi-pause-circle text-warning"
              style={{ fontSize: '16px' }}
              data-testid="upload-paused-icon"
            />
          )}

          {mode === 'slow' && (
            <i
              className="bi bi-info-circle text-warning"
              style={{ fontSize: '16px' }}
              data-testid="upload-slow-icon"
            />
          )}

          <span
            className={`fw-bold ${mode === 'paused' || mode === 'slow' ? 'text-warning' : 'text-primary'}`}
            style={{ fontSize: '13px' }}
            data-testid="upload-progress-status"
          >
            {statusText}
          </span>
        </div>

        {estimatedTime && showProgressBar && (
          <span className="text-muted" style={{ fontSize: '12px' }} data-testid="upload-estimated-time">
            {estimatedTime} remaining
          </span>
        )}
      </div>
    </div>
  )
}

export default UploadProgressBar
